import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, RefreshCw } from 'lucide-react';
import type { CalendarEntry, Content, Publication, State } from '../shared/model';
import { stageLabels } from '../shared/model';
import { operationalCalendar, type PublicationPackage } from '../shared/campaigns';
import type { CampaignDistribution } from '../shared/distribution';
import {
  addCalendarDays,
  calendarAccounts,
  calendarLabel,
  calendarMonth,
  calendarWeek,
  moveCalendarMonth,
  publicationCalendarDay,
} from '../shared/calendar-view';
import { formatClass } from './identity';
import './calendar.css';

type View = 'month' | 'week' | 'list';
type CalendarModal =
  | { kind: 'content'; item: Content }
  | { kind: 'date'; item?: Content; date?: string }
  | { kind: 'history'; entry: CalendarEntry; label: string; reference?: boolean };
type AgendaEntry = CalendarEntry & {
  key: string;
  kind: 'plan' | 'publication';
  publication?: Publication;
};
interface Props {
  data: State;
  open: (modal: CalendarModal) => void;
  filters: {
    search: string;
    campaign: string;
    format: string;
    stage: string;
    owner: string;
    showDiscarded: boolean;
  };
}
export function CalendarWorkspace({ data, open, filters }: Props) {
  const [anchor, setAnchor] = useState(data.today);
  const [view, setView] = useState<View>(() =>
    window.matchMedia('(max-width: 700px)').matches ? 'list' : 'month',
  );
  const [version, setVersion] = useState('vigente');
  const [selectedDay, setSelectedDay] = useState(data.today);
  const [account, setAccount] = useState(''),
    [accountLabel, setAccountLabel] = useState('');
  const [showPublications, setShowPublications] = useState(false);
  const [packages, setPackages] = useState<Record<string, PublicationPackage[]>>({});
  const [loadingAccounts, setLoadingAccounts] = useState(true),
    [failedCampaigns, setFailedCampaigns] = useState<string[]>([]),
    [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoadingAccounts(true);
    void Promise.allSettled(
      data.campaigns.map(async (campaign) => {
        const response = await fetch(
          `/api/campaigns/${encodeURIComponent(campaign.id)}/distribution`,
          { signal: controller.signal },
        );
        if (!response.ok) throw new Error('Destinos indisponíveis');
        const value = (await response.json()) as CampaignDistribution;
        if (!Array.isArray(value.packages)) throw new Error('Destinos indisponíveis');
        return { id: campaign.id, value: value.packages };
      }),
    ).then((results) => {
      if (controller.signal.aborted) return;
      setPackages((previous) =>
        Object.fromEntries(
          data.campaigns.map((campaign, index) => {
            const result = results[index];
            return [
              campaign.id,
              result.status === 'fulfilled' ? result.value.value : (previous[campaign.id] ?? []),
            ];
          }),
        ),
      );
      setFailedCampaigns(
        results.flatMap((result, index) =>
          result.status === 'rejected' ? [data.campaigns[index].name] : [],
        ),
      );
      setLoadingAccounts(false);
    });
    return () => controller.abort();
  }, [data, retry]);
  const accounts = useMemo(
    () =>
      calendarAccounts(
        data.publications,
        Object.values(packages).flat(),
        data.integration.accounts,
      ),
    [data.publications, data.integration.accounts, packages],
  );
  const current = data.calendars.find((calendar) => calendar.kind === 'vigente');
  const reference = version === 'vigente',
    operational = version === 'operacional',
    live = reference || operational;
  const selected = reference
    ? current
    : operational
      ? { ...current, label: 'Planejamento operacional', entries: operationalCalendar(data) }
      : data.calendars.find((calendar) => calendar.id === version);
  const editable = live && data.permissions.edit;
  const accountComplete = !loadingAccounts && !failedCampaigns.length;
  const acceptsAccount = (contentId: string) =>
    !account ||
    (account === 'unassigned'
      ? accountComplete && !accounts.contentKeys[contentId]?.length
      : accounts.contentKeys[contentId]?.includes(account));
  const accepts = (entry: CalendarEntry, publication?: Publication) => {
    const content = data.contents.find((item) => item.id === entry.contentId);
    const stage = publication ? content?.stage : operational ? content?.stage : entry.stage;
    return (
      (!filters.search ||
        `${entry.title} ${content?.flgId ?? ''} ${content?.owner ?? ''}`
          .toLocaleLowerCase('pt-BR')
          .includes(filters.search.toLocaleLowerCase('pt-BR'))) &&
      (!filters.campaign ||
        (publication
          ? (data.publicationCampaignIds?.[publication.id] ?? content?.campaignIds)
          : content?.campaignIds
        )?.includes(filters.campaign)) &&
      (!filters.format ||
        entry.format
          .toLocaleLowerCase('pt-BR')
          .includes(filters.format.toLocaleLowerCase('pt-BR'))) &&
      (!filters.stage ||
        (filters.stage === 'production'
          ? !!stage && !['publicado', 'medido', 'descartado'].includes(stage)
          : stage === filters.stage)) &&
      (!filters.owner ||
        (filters.owner === 'none' ? !content?.owner : content?.owner === filters.owner)) &&
      (filters.showDiscarded || filters.stage === 'descartado' || stage !== 'descartado') &&
      (publication && account && account !== 'unassigned'
        ? accounts.publicationKeys[publication.id] === account
        : acceptsAccount(entry.contentId))
    );
  };
  const entries: AgendaEntry[] = (selected?.entries ?? [])
    .filter((entry) => accepts(entry))
    .map((entry) => ({ ...entry, key: `plan:${entry.contentId}:${entry.date}`, kind: 'plan' }));
  if (showPublications && live)
    for (const publication of data.publications) {
      const content = data.contents.find((item) => item.id === publication.contentId);
      if (!content) continue;
      const entry: AgendaEntry = {
        key: `publication:${publication.id}`,
        kind: 'publication',
        contentId: content.id,
        title: content.title,
        format: content.format,
        stage: content.stage,
        reserve: content.reserve,
        date: publicationCalendarDay(publication),
        publication,
      };
      if (accepts(entry, publication)) entries.push(entry);
    }
  entries.sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      a.kind.localeCompare(b.kind) ||
      a.title.localeCompare(b.title, 'pt-BR'),
  );
  const month = anchor.slice(0, 7),
    week = calendarWeek(anchor);
  const inPeriod = (entry: AgendaEntry) =>
    view === 'week' ? entry.date >= week[0] && entry.date <= week[6] : entry.date.startsWith(month);
  const visible = entries.filter(inPeriod),
    days = view === 'week' ? week : calendarMonth(anchor);
  const periodLabel =
    view === 'week'
      ? `${calendarLabel(week[0])} – ${calendarLabel(week[6], { day: 'numeric', month: 'short', year: 'numeric' })}`
      : calendarLabel(anchor, { month: 'long', year: 'numeric' });
  const move = (delta: number) => {
    const next =
      view === 'week' ? addCalendarDays(anchor, delta * 7) : moveCalendarMonth(anchor, delta);
    setAnchor(next);
    setSelectedDay(next);
  };
  const openEntry = (entry: AgendaEntry) => {
    const content = data.contents.find((item) => item.id === entry.contentId);
    if (live && content) open({ kind: 'content', item: content });
    else
      open({ kind: 'history', entry, label: selected?.label ?? 'Versão do calendário', reference });
  };
  const canMove = (entry: AgendaEntry) =>
    editable &&
    entry.kind === 'plan' &&
    data.contents.some(
      (item) =>
        item.id === entry.contentId &&
        !['publicado', 'medido', 'descartado'].includes(item.stage) &&
        !item.conflict &&
        !item.sourceMissing,
    );
  const renderEntry = (entry: AgendaEntry) => {
    const content = data.contents.find((item) => item.id === entry.contentId);
    const stage = operational ? content?.stage : entry.stage;
    const details = entry.publication;
    return (
      <button
        key={entry.key}
        className={`cal-entry ${formatClass(entry.format)} ${entry.kind === 'publication' ? 'cal-published' : ''} ${entry.reserve ? 'cal-reserve' : ''}`}
        onClick={() => openEntry(entry)}
        draggable={canMove(entry)}
        onDragStart={(event) => event.dataTransfer.setData('text/flg-content', entry.contentId)}
      >
        <span className="cal-entry-kind">
          {details
            ? 'Publicação registrada'
            : operational
              ? 'Previsto na operação'
              : 'Data prevista no acervo'}
        </span>
        <strong>
          {content?.flgId ? `${content.flgId} · ` : ''}
          {entry.title}
        </strong>
        <span>
          {entry.format}
          {entry.reserve ? ' · Reserva' : ''}
          {stage ? ` · ${stageLabels[stage]}` : ' · Etapa não informada'}
        </span>
        {details ? (
          <span className="cal-publication-details">
            {details.channel} · {details.account} · {calendarLabel(entry.date)}
            {details.precision === 'second' && details.publishedAt
              ? ` às ${new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' }).format(new Date(details.publishedAt))}`
              : ' · hora não informada'}
          </span>
        ) : (
          <span>{content?.owner || 'Responsável a definir'}</span>
        )}
      </button>
    );
  };
  const renderDay = (date: string) => {
    const daily = entries.filter((entry) => entry.date === date),
      outside = !date.startsWith(month) && view === 'month';
    return (
      <section
        key={date}
        className={`cal-day ${outside ? 'cal-outside' : ''} ${date === data.today ? 'cal-today' : ''} ${date === selectedDay ? 'cal-selected' : ''}`}
        aria-label={calendarLabel(date, { weekday: 'long', day: 'numeric', month: 'long' })}
        onDragOver={(event) => {
          if (editable) event.preventDefault();
        }}
        onDrop={(event) => {
          event.preventDefault();
          const item = data.contents.find(
            (content) => content.id === event.dataTransfer.getData('text/flg-content'),
          );
          if (
            editable &&
            item &&
            !['publicado', 'medido', 'descartado'].includes(item.stage) &&
            !item.conflict &&
            !item.sourceMissing
          )
            open({ kind: 'date', item, date });
        }}
      >
        <div className="cal-day-heading">
          <span>
            {view === 'week'
              ? calendarLabel(date, { weekday: 'short', day: 'numeric', month: 'short' })
              : Number(date.slice(-2))}
          </span>
          {editable && (
            <button aria-label={`Propor data ${date}`} onClick={() => open({ kind: 'date', date })}>
              <Plus size={13} />
            </button>
          )}
        </div>
        <button
          className="cal-mobile-day"
          aria-label={`Ver ${calendarLabel(date, { day: 'numeric', month: 'long', year: 'numeric' })}, ${daily.length} registro${daily.length === 1 ? '' : 's'}`}
          aria-pressed={date === selectedDay}
          onClick={() => setSelectedDay(date)}
        >
          <strong>{Number(date.slice(-2))}</strong>
          <span>{daily.length || '—'}</span>
        </button>
        <div className="cal-day-entries">
          {daily.map(renderEntry)}
          {view === 'week' && !daily.length && <p className="cal-empty-day">Sem registros</p>}
        </div>
      </section>
    );
  };
  return (
    <section className={`cal-workspace cal-view-${view}`} aria-label="Calendário editorial">
      <div className="cal-controls">
        <div className="cal-view-switch" role="group" aria-label="Visualização do calendário">
          {(
            [
              ['month', 'Mês'],
              ['week', 'Semana'],
              ['list', 'Lista'],
            ] as const
          ).map(([value, label]) => (
            <button key={value} aria-pressed={view === value} onClick={() => setView(value)}>
              {label}
            </button>
          ))}
        </div>
        <label>
          Versão do calendário
          <select
            aria-label="Versão do calendário"
            value={version}
            onChange={(event) => setVersion(event.target.value)}
          >
            <option value="vigente">Acervo vigente</option>
            <option value="operacional">Planejamento operacional · simulação</option>
            {data.calendars
              .filter((calendar) => calendar.kind !== 'vigente')
              .map((calendar) => (
                <option key={calendar.id} value={calendar.id}>
                  {calendar.kind === 'proposto' ? 'Proposta · ' : 'Histórico · '}
                  {calendar.label}
                  {calendar.kind === 'substituido' ? ` · ${calendar.source.hash.slice(0, 6)}` : ''}
                </option>
              ))}
          </select>
        </label>
        <label>
          Conta
          <select
            aria-label="Filtrar conta do calendário"
            value={account}
            onChange={(event) => {
              setAccount(event.target.value);
              setAccountLabel(event.target.selectedOptions[0]?.textContent ?? 'Conta selecionada');
            }}
          >
            <option value="">Todas as contas registradas</option>
            <option value="unassigned" disabled={!accountComplete}>
              Sem conta documentada
            </option>
            {accounts.options.map((option) => (
              <option key={option.key} value={option.key}>
                {option.channel} · {option.account}
                {option.sources.includes('publication') ? ' · publicação' : ' · destino planejado'}
                {option.verified ? ' · verificada' : ''}
              </option>
            ))}
            {account &&
              account !== 'unassigned' &&
              !accounts.options.some((option) => option.key === account) && (
                <option value={account}>{accountLabel} · vínculo indisponível</option>
              )}
          </select>
        </label>
      </div>
      <div className="cal-period">
        <button
          aria-label={view === 'week' ? 'Semana anterior' : 'Mês anterior'}
          onClick={() => move(-1)}
        >
          <ChevronLeft size={18} />
        </button>
        <h2 aria-live="polite">{periodLabel}</h2>
        <button
          aria-label={view === 'week' ? 'Próxima semana' : 'Próximo mês'}
          onClick={() => move(1)}
        >
          <ChevronRight size={18} />
        </button>
        <button
          onClick={() => {
            setAnchor(data.today);
            setSelectedDay(data.today);
          }}
        >
          Hoje
        </button>
      </div>
      <div className="cal-context">
        <strong>
          {reference
            ? 'Acervo vigente'
            : operational
              ? 'Planejamento operacional · simulação'
              : 'Versão preservada · consulta'}
        </strong>
        <p>
          {reference
            ? 'Datas e etapas da versão vigente importada. Propor uma mudança preserva esta referência até a incorporação no acervo.'
            : operational
              ? 'Combina a referência importada com datas aprovadas no painel. Essas mudanças aguardam registro e reimportação no acervo.'
              : 'Datas, etapas e reservas preservadas nesta versão. A consulta não altera o calendário vigente.'}
        </p>
      </div>
      {loadingAccounts && (
        <p className="cal-account-notice" role="status">
          Consultando destinos registrados nas campanhas…
        </p>
      )}
      {!!failedCampaigns.length && (
        <div className="cal-account-notice cal-warning" role="status">
          <span>
            Destinos de {failedCampaigns.length} campanha(s) indisponíveis. O filtro por conta
            mantém as fontes já consultadas e pode estar incompleto.
          </span>
          <button onClick={() => setRetry((value) => value + 1)}>
            <RefreshCw size={14} /> Consultar novamente
          </button>
        </div>
      )}
      <div className="cal-options">
        <span>
          Contas vêm de publicações e destinos explícitos; colaboradores não são destinos. Vínculos
          de conta, campanha e responsável são atuais.
        </span>
        <label className="check">
          <input
            type="checkbox"
            checked={showPublications && live}
            disabled={!live}
            onChange={(event) => setShowPublications(event.target.checked)}
          />
          Sobrepor publicações registradas
        </label>
      </div>
      {showPublications && live && (
        <p className="cal-overlay-note">
          Publicações aparecem na data real, separadas das datas previstas. Uma peça pode aparecer
          mais de uma vez por ter mais de um destino.
        </p>
      )}
      {!selected && (
        <p className="cal-account-notice">
          Nenhuma versão do acervo disponível. Importe o acervo em Conexões; o planejamento
          operacional continua separado.
        </p>
      )}
      {view === 'list' ? (
        <div className="cal-list" aria-label="Lista do mês">
          {[...new Set(visible.map((entry) => entry.date))].map((date) => (
            <section className="cal-list-day" key={date}>
              <h3>{calendarLabel(date, { weekday: 'long', day: 'numeric', month: 'short' })}</h3>
              <div>{visible.filter((entry) => entry.date === date).map(renderEntry)}</div>
            </section>
          ))}
        </div>
      ) : (
        <div
          className={`cal-grid cal-${view}`}
          aria-label={view === 'week' ? 'Semana do calendário' : 'Mês do calendário'}
        >
          {view === 'month' &&
            ['SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB', 'DOM'].map((day) => (
              <div key={day} className="cal-weekday">
                {day}
              </div>
            ))}
          {days.map(renderDay)}
        </div>
      )}
      {view === 'month' && (
        <section className="cal-selected-agenda" aria-label="Registros do dia selecionado">
          <h3>{calendarLabel(selectedDay, { weekday: 'long', day: 'numeric', month: 'long' })}</h3>
          {entries.filter((entry) => entry.date === selectedDay).map(renderEntry)}
          {!entries.some((entry) => entry.date === selectedDay) && (
            <p>Nenhum registro neste dia com os filtros atuais.</p>
          )}
          {editable && (
            <button onClick={() => open({ kind: 'date', date: selectedDay })}>
              <Plus size={14} /> Propor data neste dia
            </button>
          )}
        </section>
      )}
      {!visible.length && (
        <div className="cal-empty">
          <strong>
            Nenhum registro {view === 'week' ? 'nesta semana' : 'neste mês'} com os filtros atuais.
          </strong>
          <p>Altere o período ou os filtros para consultar a versão selecionada.</p>
        </div>
      )}
      <footer className="cal-footer">
        <span>
          {visible.length} registro{visible.length === 1 ? '' : 's'}{' '}
          {view === 'week' ? 'na semana' : 'no mês'} · Datas sem hora permanecem datas de
          calendário. Horários conhecidos em São Paulo.
        </span>
        {editable && (
          <button onClick={() => open({ kind: 'date' })}>
            <Plus size={14} />
            Propor data
          </button>
        )}
      </footer>
    </section>
  );
}
